import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import AccountCirleIcon from '@mui/icons-material/AccountCircle'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import MenuIcon from '@mui/icons-material/Menu'
import AppBar from '@mui/material/AppBar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'

import { useProfile } from '../account/hooks/useProfile'
import { isProfileIncomplete } from '../account/util/profile-util'
import { LoginButton } from '../auth/components/LoginButton'
import { useAuthenticatedUser } from '../auth/hooks/useAuthenticatedUser'
import { useUserParam } from '../auth/hooks/useUserParam'
import { useLocation } from '../router/hooks/useLocation'
import { useNavigate } from '../router/hooks/useNavigate'
import { CapoLogo } from '../theme/components/CapoLogo'

import { Profile } from './components/Profile'

// The toolbar height is pinned for all breakpoints, so sticky elements below
// the AppBar can rely on it. The AppBar adds a 1px divider below the toolbar.
export const TOOLBAR_HEIGHT = 64
export const APP_BAR_HEIGHT = TOOLBAR_HEIGHT + 1

type Props = {
  onMenuClick?: () => void
  breadcrumbsSlotRef?: (element: HTMLElement | null) => void
}

export const Navigation = ({ onMenuClick, breadcrumbsSlotRef }: Props) => {
  const { hasPermissions } = useAuthenticatedUser()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const { userParam, storeUserParam } = useUserParam()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data: profile } = useProfile()
  const profileIncomplete = isProfileIncomplete(profile)

  const handleChangeUserParam = (value: string) => {
    storeUserParam(value)
    queryClient.refetchQueries()
  }

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <AppBar position="sticky" elevation={0} sx={{ background: 'white' }}>
      <Toolbar
        sx={{ color: 'black', gap: 2, minHeight: { xs: TOOLBAR_HEIGHT } }}
      >
        {onMenuClick && (
          <IconButton
            data-testid="menuButton"
            edge="start"
            color="inherit"
            onClick={onMenuClick}
          >
            <MenuIcon />
          </IconButton>
        )}

        <CapoLogo
          onClick={pathname !== '/home' ? () => navigate('/home') : undefined}
        />

        <Box
          ref={breadcrumbsSlotRef}
          sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}
        />

        {hasPermissions ? (
          <>
            <Tooltip
              title={profileIncomplete ? 'Accountgegevens aanvullen' : ''}
            >
              <IconButton
                color="inherit"
                onClick={handleMenu}
                data-testid="accountButton"
              >
                <Badge
                  variant="dot"
                  color="error"
                  invisible={!profileIncomplete}
                >
                  <AccountCirleIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Popover
              data-testid="accountPopover"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Profile
                onChangeUserParam={handleChangeUserParam}
                userParam={userParam}
              />
            </Popover>
          </>
        ) : (
          <>
            <Button
              href="https://mijnaansluiting.atlassian.net/wiki/spaces/Wiki/pages/40075267/Registreren+als+gebruiker+van+CAPO"
              target="_blank"
              rel="noreferrer"
              sx={{ color: 'black' }}
              color="secondary"
              startIcon={<AssignmentIndIcon />}
            >
              Registeren voor CAPO
            </Button>

            <LoginButton sx={{ color: 'black' }} />
          </>
        )}
      </Toolbar>

      <Divider />
    </AppBar>
  )
}
